import { CartesianGrid, ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis, ZAxis } from "recharts";
import { Paper, Typography, useTheme } from "@mui/material";
import { useChartPalette } from "../utils/chartUtils";
import { EmptyChartState } from "@/components/common/EmptyChartState";
import { formatUSD } from "@/utils/formatters/currencyFormatter";

type ExceedancePoint = {
  month: string;
  deductible: number;
  crossed_count: number;
  total_over: number;
};

type Props = {
  data: ExceedancePoint[];
  isFullscreen: boolean;
};

// "YYYY-MM" -> "Aug 25", matching the other claim charts' month axis.
const formatYearMonth = (ym?: string) => {
  if (!ym) return "";
  const [year, month] = ym.split("-");
  if (!year || !month) return ym;
  return new Date(Number(year), Number(month) - 1).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
};

const compactUSD = (v: number) =>
  v >= 1000 ? `$${(v / 1000).toLocaleString(undefined, { maximumFractionDigits: 0 })}k` : formatUSD(v);

const BubbleTooltip = ({ active, payload }: { active?: boolean; payload?: { payload: ExceedancePoint }[] }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  const avg = p.crossed_count > 0 ? p.total_over / p.crossed_count : 0;
  return (
    <Paper elevation={3} sx={{ p: 1.5, maxWidth: 260 }}>
      <Typography variant="subtitle2">
        {formatYearMonth(p.month)} · {formatUSD(p.deductible)} deductible
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        <strong>{p.crossed_count.toLocaleString()}</strong> claimant(s) over deductible
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {formatUSD(p.total_over)} total over · {formatUSD(avg)} avg / claimant
      </Typography>
    </Paper>
  );
};

/**
 * Deductible-breach bubbles: x = month (authoritative as-of), y = the claimant's
 * effective deductible (exclusion > contract laser > straight laser > base, so an
 * incomplete laser shows the base), bubble size = how many lasered claimants are
 * over that deductible that month. The overage is the authority-resolved figure the
 * engine uses (coronis-apps #7391), the same one the lasered-claimants table beside
 * it shows, so the two reconcile.
 */
const LaseredExceedanceChart = ({ data, isFullscreen }: Props) => {
  const palette = useChartPalette();
  const theme = useTheme();
  if (!data.length) return <EmptyChartState />;

  const maxCount = Math.max(...data.map((d) => d.crossed_count), 1);

  return (
    <ResponsiveContainer width="100%" height={isFullscreen ? 500 : "100%"}>
      <ScatterChart margin={{ top: 16, right: 24, left: 16, bottom: 8 }}>
        <defs>
          <radialGradient id="breachBubbleFill" cx="35%" cy="35%" r="65%">
            <stop offset="0%" stopColor={palette[0]} stopOpacity={0.85} />
            <stop offset="100%" stopColor={palette[0]} stopOpacity={0.35} />
          </radialGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
        <XAxis
          type="category"
          dataKey="month"
          tickFormatter={formatYearMonth}
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
          allowDuplicatedCategory={false}
        />
        <YAxis
          type="number"
          dataKey="deductible"
          name="Deductible"
          tickFormatter={compactUSD}
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
          label={{
            value: "Effective deductible",
            angle: -90,
            position: "insideLeft",
            style: { fontSize: 11, fill: theme.palette.text.secondary, textAnchor: "middle" },
          }}
        />
        <ZAxis type="number" dataKey="crossed_count" range={[120, 900]} domain={[0, maxCount]} name="Claimants" />
        <Tooltip content={<BubbleTooltip />} cursor={{ strokeDasharray: "3 3" }} />
        <Scatter data={data} fill="url(#breachBubbleFill)" stroke={palette[0]} strokeOpacity={0.9} />
      </ScatterChart>
    </ResponsiveContainer>
  );
};

export default LaseredExceedanceChart;
